import type { JsonField, NullableJsonFieldType } from "./model"
import { JSON_STRING_FORMATS, JsonFieldType, NULL } from "./model"

export const isNotNullType = <T extends JsonFieldType>(
  value: T | typeof NULL,
): value is T => value !== NULL

export function isFieldOfType<T extends JsonFieldType>(
  field: JsonField,
  type: T,
): field is Extract<JsonField, { type: NullableJsonFieldType<T> }> {
  const t = field.type

  return Array.isArray(t) ? t.some((value) => value === type) : t === type
}

export function isNullable<T extends JsonFieldType>(
  type: NullableJsonFieldType<T>,
): boolean {
  return Array.isArray(type) && type.includes(NULL)
}

export function getBaseType<T extends JsonFieldType>(
  type: NullableJsonFieldType<T>,
): T {
  if (!Array.isArray(type)) {
    return type
  }

  const baseType = type.find(isNotNullType)
  if (!baseType) {
    throw new Error("Nullable JsonFieldType must include a base type")
  }

  return baseType
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

export const isJsonFieldType = (value: unknown): value is JsonFieldType =>
  typeof value === "string" &&
  (Object.values(JsonFieldType) as string[]).includes(value)

export const isNullableType = (
  value: unknown,
): value is [JsonFieldType, typeof NULL] | [typeof NULL, JsonFieldType] =>
  Array.isArray(value) &&
  value.length === 2 &&
  value.includes(NULL) &&
  value.some(isJsonFieldType)

const baseType = (value: JsonField["type"]): JsonFieldType =>
  Array.isArray(value) ? (value.find(isJsonFieldType) as JsonFieldType) : value

const isOptionalEnum = (
  value: unknown,
  isMember: (entry: unknown) => boolean,
): boolean =>
  typeof value === "undefined" ||
  (Array.isArray(value) && value.every(isMember))

const isStringMember = (entry: unknown) => typeof entry === "string"
const isNumberMember = (entry: unknown) => typeof entry === "number"

export const isJsonField = (value: unknown): value is JsonField => {
  if (!isObject(value)) {
    return false
  }

  const type = value.type
  if (!isJsonFieldType(type) && !isNullableType(type)) {
    return false
  }

  const resolvedBaseType = baseType(type)

  if (
    typeof value.description !== "undefined" &&
    typeof value.description !== "string"
  ) {
    return false
  }

  switch (resolvedBaseType) {
    case JsonFieldType.string:
      return (
        (typeof value.minLength === "undefined" ||
          typeof value.minLength === "number") &&
        (typeof value.maxLength === "undefined" ||
          typeof value.maxLength === "number") &&
        (typeof value.format === "undefined" ||
          (typeof value.format === "string" &&
            (JSON_STRING_FORMATS as readonly string[]).includes(
              value.format,
            ))) &&
        isOptionalEnum(value.enum, isStringMember)
      )
    case JsonFieldType.number:
    case JsonFieldType.integer:
      return (
        (typeof value.minimum === "undefined" ||
          typeof value.minimum === "number") &&
        (typeof value.maximum === "undefined" ||
          typeof value.maximum === "number") &&
        isOptionalEnum(
          value.enum,
          resolvedBaseType === JsonFieldType.integer
            ? Number.isInteger
            : isNumberMember,
        )
      )
    case JsonFieldType.boolean:
      return true
    case JsonFieldType.object: {
      if (
        !isObject(value.properties) ||
        !Array.isArray(value.required) ||
        value.additionalProperties !== false ||
        !value.required.every((entry) => typeof entry === "string")
      ) {
        return false
      }

      const keys = Object.keys(value.properties)

      return (
        Object.values(value.properties).every(isJsonField) &&
        value.required.every((key) => keys.includes(key))
      )
    }
    case JsonFieldType.array:
      return (
        isJsonField(value.items) &&
        (typeof value.minItems === "undefined" ||
          typeof value.minItems === "number") &&
        (typeof value.maxItems === "undefined" ||
          typeof value.maxItems === "number")
      )
  }
}

const getBaseTypeFromUnknown = (value: unknown): JsonFieldType | undefined => {
  if (isJsonFieldType(value)) {
    return value
  }
  if (Array.isArray(value)) {
    return value.find(isJsonFieldType)
  }

  return undefined
}

export const normalizeJsonFieldShape = (value: unknown): void => {
  if (!isObject(value)) {
    return
  }

  const baseType = getBaseTypeFromUnknown(value.type)
  if (!baseType) {
    return
  }

  if (baseType === JsonFieldType.array) {
    if (!isObject(value.items)) {
      value.items = { type: JsonFieldType.string }
    }
    normalizeJsonFieldShape(value.items)
  }

  if (baseType === JsonFieldType.object) {
    if (!isObject(value.properties)) {
      value.properties = {}
    }
    if (!Array.isArray(value.required)) {
      value.required = []
    }
    if (value.additionalProperties !== false) {
      value.additionalProperties = false
    }

    const props = value.properties as Record<string, unknown>

    value.required = Object.keys(props)

    Object.values(props).forEach((prop) => {
      normalizeJsonFieldShape(prop)
    })
  }
}

export const stringifySchema = (schema: JsonField) =>
  JSON.stringify(schema, null, 2)
